import React, { useEffect, useMemo, useState } from 'react';
import {
  Calculator, Loader2, Package, Search, X, AlertCircle, Info,
  TrendingDown, ArrowRight, CheckCircle2, Sparkles, ShoppingBag, Truck, Wallet, Weight,
} from 'lucide-react';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';
import { useSeller } from '../../context/SellerContext';
import pricingCalculatorService from '../../services/pricingCalculatorService';
import {
  CLOSING_FEE_GROUPS,
  CLOSING_FEE_TABLE,
  EASY_SHIP_STEP_LEVELS,
  EASY_SHIP_BASE,
  RATE_CARDS_LAST_UPDATED,
  AMAZON_SERVICE_GST_PCT,
  lookupClosingFee,
  lookupEasyShipFee,
  describeClosingFeeBand,
  describeEasyShipBand,
  withServiceGst,
  serviceGstOf,
} from '../../data/amazonRateCards';

const fmt = (val) => {
  const n = Number(val ?? 0);
  if (Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
const fmtPct = (val) => (val == null ? '—' : `${Number(val).toFixed(1)}%`);

const GST_RATES = [5, 12, 18, 28];
const FULFILLMENT_OPTIONS = [
  { key: 'MERCHANT',  label: 'Self (FBM)',   icon: ShoppingBag, hint: 'No FBA / Easy-Ship charge' },
  { key: 'EASY_SHIP', label: 'Easy Ship',    icon: Truck,       hint: 'Amazon picks up, you pack' },
  { key: 'FBA',       label: 'FBA',          icon: Package,     hint: 'Amazon stores & ships' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip — small inline info popover that opens on hover/click. Pure CSS.
// ─────────────────────────────────────────────────────────────────────────────

function Tooltip({ children, label = 'More info', size = 12 }) {
  return (
    <span className="relative inline-flex group align-middle">
      <button
        type="button"
        aria-label={label}
        className="text-slate-400 hover:text-slate-700 focus:outline-none cursor-help"
        tabIndex={0}
      >
        <Info size={size} />
      </button>
      <span className="absolute z-30 left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 p-3 rounded-lg bg-slate-900 text-white text-[11px] leading-relaxed shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity pointer-events-none">
        {children}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-900" />
      </span>
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════════════

export default function PricingCalculatorPage() {
  const { selectedSeller } = useSeller();
  const sellerChannels = useMemo(
    () => (selectedSeller?.channels || []).filter(
      c => c.accountStatus === 'AUTHORIZED' && (c.platform === 'AMAZON' || !c.platform)
    ),
    [selectedSeller]
  );

  const [selectedAccountId, setSelectedAccountId] = useState('');
  useEffect(() => {
    if (!selectedAccountId && sellerChannels.length > 0) {
      setSelectedAccountId(sellerChannels[0].id);
    }
  }, [sellerChannels, selectedAccountId]);

  // Inputs
  const [listingPrice, setListingPrice] = useState('');
  const [gstRatePct, setGstRatePct] = useState(18);
  const [fulfillment, setFulfillment] = useState('MERCHANT');
  const [sku, setSku] = useState('');
  const [skuQuery, setSkuQuery] = useState('');
  const [showSkuDropdown, setShowSkuDropdown] = useState(false);

  // New inputs (rate-card driven)
  const [weightGrams, setWeightGrams] = useState('500');
  const [stepLevel, setStepLevel] = useState('STANDARD');
  const [closingFeeGroup, setClosingFeeGroup] = useState('STANDARD');

  // Data
  const [products, setProducts] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [spApiFees, setSpApiFees] = useState(null);
  const [error, setError] = useState(null);

  // Load products list once per seller account.
  useEffect(() => {
    if (!selectedAccountId) { setProducts([]); return; }
    let cancelled = false;
    pricingCalculatorService.listProducts(selectedAccountId)
      .then((res) => { if (!cancelled) setProducts(Array.isArray(res.data) ? res.data : (res || [])); })
      .catch(() => { if (!cancelled) setProducts([]); });
    return () => { cancelled = true; };
  }, [selectedAccountId]);

  // Recompute base estimate (historical averages from backend).
  useEffect(() => {
    if (!selectedAccountId || !listingPrice || Number(listingPrice) <= 0) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setEstimating(true);
    const handle = setTimeout(() => {
      pricingCalculatorService.estimate({
        sellerAccountId: selectedAccountId,
        sku: sku || null,
        listingPrice: Number(listingPrice),
        gstRatePct,
        fulfillment,
      })
        .then((res) => { if (!cancelled) setEstimate(res.data || res); })
        .catch((err) => {
          if (!cancelled) setError(err?.response?.data?.message || 'Failed to compute estimate');
        })
        .finally(() => { if (!cancelled) setEstimating(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [selectedAccountId, listingPrice, sku, gstRatePct, fulfillment]);

  // When SKU is selected and listingPrice is set, fetch live SP-API fees for accuracy.
  useEffect(() => {
    if (!selectedAccountId || !sku || !listingPrice || Number(listingPrice) <= 0) {
      setSpApiFees(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      pricingCalculatorService.getSpApiFees(
        selectedAccountId,
        sku,
        Number(listingPrice),
        fulfillment === 'FBA',
      )
        .then((res) => { if (!cancelled) setSpApiFees(res.data || res); })
        .catch(() => { if (!cancelled) setSpApiFees(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [selectedAccountId, sku, listingPrice, fulfillment]);

  const filteredProducts = useMemo(() => {
    if (!skuQuery) return products.slice(0, 30);
    const q = skuQuery.toLowerCase();
    return products
      .filter(p =>
        p.sku?.toLowerCase().includes(q) ||
        p.title?.toLowerCase().includes(q) ||
        p.asin?.toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [products, skuQuery]);

  const selectedProduct = sku ? products.find(p => p.sku === sku) : null;

  const handleClearSku = () => {
    setSku('');
    setSkuQuery('');
    setShowSkuDropdown(false);
  };

  // ── Compose the final waterfall: backend's averages, overlaid with rate-card precision.
  const composedEstimate = useMemo(() => {
    if (!estimate) return null;
    return composeEstimate({
      estimate,
      spApiFees,
      listingPrice: Number(listingPrice) || 0,
      fulfillment,
      weightGrams: Number(weightGrams) || 0,
      stepLevel,
      closingFeeGroup,
    });
  }, [estimate, spApiFees, listingPrice, fulfillment, weightGrams, stepLevel, closingFeeGroup]);

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* ─── Header ──────────────────────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Calculator size={20} className="text-[#f08804]" />
              Pricing Calculator
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Type a listing price, see what actually lands in your bank after Amazon's fees, GST, TCS, and TDS
            </p>
          </div>
          <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />
        </div>

        {!selectedAccountId ? (
          <div className="p-10 text-center bg-white rounded-xl border border-slate-200">
            <AlertCircle size={28} className="mx-auto text-amber-500 mb-2" />
            <p className="text-sm text-slate-700">Select an Amazon account above to begin</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* ═══════════ Left: Inputs (2 cols) ═══════════ */}
            <div className="lg:col-span-2 space-y-3">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-4">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    Listing price (MRP, ₹)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-semibold">₹</span>
                    <input
                      type="number"
                      value={listingPrice}
                      onChange={(e) => setListingPrice(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg text-base font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    What the customer sees on Amazon — incl. GST
                  </p>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    GST rate
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {GST_RATES.map(r => (
                      <button
                        key={r}
                        onClick={() => setGstRatePct(r)}
                        className={`py-1.5 rounded text-xs font-semibold border transition-colors ${
                          gstRatePct === r
                            ? 'bg-[#FFF4E6] border-[#f08804] text-[#C45500]'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {r}%
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    Fulfilment
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {FULFILLMENT_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      const active = fulfillment === opt.key;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => setFulfillment(opt.key)}
                          className={`flex flex-col items-center gap-1 py-2 rounded text-[10px] font-semibold border transition-colors ${
                            active
                              ? 'bg-[#FFF4E6] border-[#f08804] text-[#C45500]'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                          title={opt.hint}
                        >
                          <Icon size={14} />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* FBA explainer — surfaces what's actually inside the FBA fee % we show */}
                {fulfillment === 'FBA' && (
                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-sky-800 uppercase tracking-wider mb-2">
                      <Package size={12} /> What's inside the FBA fee
                      <Tooltip label="FBA fee components">
                        <div className="font-bold mb-1">Amazon FBA fee has 3 components:</div>
                        <ul className="text-[11px] space-y-1.5 mt-1">
                          <li><strong className="text-amber-300">Pick &amp; Pack</strong> — per unit sold. ₹17 for Standard items ≤1kg, ₹5 / addl kg up to 5kg, ₹2 / addl 5kg thereafter. Heavy/Bulky ≤12kg: ₹26.</li>
                          <li><strong className="text-amber-300">Weight Handling</strong> — Amazon shipping the item to the customer (separate weight × zone slabs).</li>
                          <li><strong className="text-amber-300">Storage</strong> — per cubic foot per month while goods sit in Amazon's FC (not in this calculator — applies monthly).</li>
                        </ul>
                        <div className="text-amber-200 mt-2 text-[10px]">
                          When you pick a specific SKU, we call SP-API and get Amazon's exact rolled-up total — Pick &amp; Pack + Weight Handling combined, accurate per ASIN. Otherwise we use your historical FBA average from past settlements (which already includes all three).
                        </div>
                      </Tooltip>
                    </div>
                    <p className="text-[11px] text-sky-900 leading-relaxed">
                      The FBA fee shown below is the <strong>rolled-up total</strong> from Amazon —
                      includes Pick &amp; Pack, Weight Handling, and any per-item charges. Storage fees
                      are billed monthly and not included in per-order math.
                    </p>
                    <p className="text-[10px] text-sky-700 mt-1.5">
                      💡 Pick a specific SKU below for the exact per-ASIN FBA fee from Amazon SP-API.
                    </p>
                  </div>
                )}

                {/* Weight + STEP — only relevant when shipping fee applies */}
                {fulfillment === 'EASY_SHIP' && (
                  <div className="space-y-3 p-3 bg-amber-50/50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                      <Weight size={12} /> Easy Ship details
                      <Tooltip label="Easy Ship Weight Handling Fee">
                        <div className="font-bold mb-1">Easy Ship is billed by weight, not %.</div>
                        Amazon charges a flat ₹ per shipment based on the outbound weight slab and your STEP tier (values below are <strong>base, GST-exclusive</strong>):
                        <table className="w-full mt-2 text-[10px] tabular-nums">
                          <thead className="text-amber-200">
                            <tr><th className="text-left pr-1">Weight</th><th>Premium/Adv</th><th>Standard</th><th>Basic</th></tr>
                          </thead>
                          <tbody className="text-white/90">
                            <tr><td className="pr-1">First 500 g</td><td className="text-center">₹{EASY_SHIP_BASE.PREMIUM_ADVANCED.firstHalfKg}</td><td className="text-center">₹{EASY_SHIP_BASE.STANDARD.firstHalfKg}</td><td className="text-center">₹{EASY_SHIP_BASE.BASIC.firstHalfKg}</td></tr>
                            <tr><td className="pr-1">500g – 1kg</td><td className="text-center">₹{EASY_SHIP_BASE.PREMIUM_ADVANCED.halfToOneKg}</td><td className="text-center">₹{EASY_SHIP_BASE.STANDARD.halfToOneKg}</td><td className="text-center">₹{EASY_SHIP_BASE.BASIC.halfToOneKg}</td></tr>
                            <tr><td className="pr-1">1 – 2 kg</td><td className="text-center">₹{EASY_SHIP_BASE.PREMIUM_ADVANCED.oneToTwoKg}</td><td className="text-center">₹{EASY_SHIP_BASE.STANDARD.oneToTwoKg}</td><td className="text-center">₹{EASY_SHIP_BASE.BASIC.oneToTwoKg}</td></tr>
                            <tr><td className="pr-1">+ each kg ≤5</td><td colSpan={3} className="text-center">+₹34</td></tr>
                            <tr><td className="pr-1">+ each kg &gt;5</td><td colSpan={3} className="text-center">+₹18</td></tr>
                          </tbody>
                        </table>
                        <div className="text-amber-200 mt-1.5 text-[10px]">
                          + 18% GST on top — recoverable as Input Tax Credit in your GSTR-3B.<br />
                          Rate card updated {RATE_CARDS_LAST_UPDATED}.
                        </div>
                      </Tooltip>
                    </div>

                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">
                        Outbound weight (grams)
                      </label>
                      <input
                        type="number"
                        value={weightGrams}
                        onChange={(e) => setWeightGrams(e.target.value)}
                        placeholder="e.g. 500"
                        min="0"
                        step="50"
                        className="w-full px-3 py-1.5 border border-amber-300 rounded text-xs font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-500"
                      />
                      <p className="text-[10px] text-amber-700/70 mt-0.5">
                        Falls in band: <strong>{describeEasyShipBand(weightGrams) || '—'}</strong>
                      </p>
                    </div>

                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">
                        Your STEP tier
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {EASY_SHIP_STEP_LEVELS.map(s => (
                          <button
                            key={s.key}
                            onClick={() => setStepLevel(s.key)}
                            className={`py-1.5 rounded text-[10px] font-bold border transition-colors ${
                              stepLevel === s.key
                                ? 'bg-amber-500 border-amber-500 text-white'
                                : 'bg-white border-amber-300 text-amber-800 hover:bg-amber-50'
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Closing fee category group */}
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    Closing fee group{' '}
                    <Tooltip label="Closing Fee table">
                      <div className="font-bold mb-1">Closing fee depends on price and category group.</div>
                      Amazon charges a flat ₹ per item based on your sale price band (values below are <strong>base, GST-exclusive</strong>):
                      <table className="w-full mt-2 text-[10px] tabular-nums">
                        <thead className="text-amber-200">
                          <tr><th className="text-left pr-1">Item price</th><th>Standard</th><th>Select A</th><th>Select B</th></tr>
                        </thead>
                        <tbody className="text-white/90">
                          {CLOSING_FEE_TABLE.map((r, i) => (
                            <tr key={i}>
                              <td className="pr-1">
                                {r.priceMin === 0 ? '₹0–300'
                                  : r.priceMin === 301 ? '₹301–500'
                                  : r.priceMin === 501 ? '₹501–1,000'
                                  : '> ₹1,000'}
                              </td>
                              <td className="text-center">₹{r.STANDARD}</td>
                              <td className="text-center">₹{r.SELECT_A}</td>
                              <td className="text-center">₹{r.SELECT_B}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="text-amber-200 mt-1.5 text-[10px]">
                        + 18% GST on top — recoverable as Input Tax Credit in your GSTR-3B.<br />
                        Most categories use Standard. Rate card updated {RATE_CARDS_LAST_UPDATED}.
                      </div>
                    </Tooltip>
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {CLOSING_FEE_GROUPS.map(g => (
                      <button
                        key={g.key}
                        onClick={() => setClosingFeeGroup(g.key)}
                        className={`py-1.5 rounded text-[10px] font-bold border transition-colors ${
                          closingFeeGroup === g.key
                            ? 'bg-[#FFF4E6] border-[#f08804] text-[#C45500]'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                        title={g.description}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    Use rates from a specific SKU <span className="font-normal text-slate-400 normal-case">(optional)</span>
                  </label>
                  {selectedProduct ? (
                    <div className="flex items-center gap-2 px-2 py-1.5 border border-[#f08804] rounded-lg bg-[#FFF4E6]">
                      {selectedProduct.imageUrl ? (
                        <img src={selectedProduct.imageUrl} alt="" className="w-8 h-8 rounded border border-slate-200 object-contain bg-white" />
                      ) : (
                        <div className="w-8 h-8 rounded border border-slate-200 bg-white flex items-center justify-center">
                          <Package size={12} className="text-slate-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-slate-700 truncate" title={selectedProduct.title}>
                          {selectedProduct.title || selectedProduct.sku}
                        </p>
                        <p className="text-[10px] text-slate-500 font-mono truncate">
                          {selectedProduct.sku}
                        </p>
                      </div>
                      <button onClick={handleClearSku} className="text-slate-500 hover:text-rose-600 p-1">
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={skuQuery}
                        onChange={(e) => { setSkuQuery(e.target.value); setShowSkuDropdown(true); }}
                        onFocus={() => setShowSkuDropdown(true)}
                        onBlur={() => setTimeout(() => setShowSkuDropdown(false), 150)}
                        placeholder="Search SKU, ASIN, or title…"
                        className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
                      />
                      {showSkuDropdown && filteredProducts.length > 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                          {filteredProducts.map(p => (
                            <button
                              key={p.sku}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { setSku(p.sku); setSkuQuery(''); setShowSkuDropdown(false); }}
                              className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 text-left border-b border-slate-100 last:border-b-0"
                            >
                              {p.imageUrl ? (
                                <img src={p.imageUrl} alt="" className="w-7 h-7 rounded border border-slate-200 object-contain bg-white flex-shrink-0" />
                              ) : (
                                <div className="w-7 h-7 rounded border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                                  <Package size={10} className="text-slate-300" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold text-slate-700 truncate">{p.title || p.sku}</p>
                                <p className="text-[10px] text-slate-500 font-mono truncate">
                                  {p.sku}{p.asin && <> · {p.asin}</>}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showSkuDropdown && skuQuery && filteredProducts.length === 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-[11px] text-slate-500 italic">
                          No products match — calculator will use seller-wide averages
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1">
                    Pick a SKU to use Amazon's official per-ASIN fees (live SP-API). Otherwise we use historical averages.
                  </p>
                </div>
              </div>

              <RatesPanel rates={estimate?.rates} spApiFees={spApiFees} />
            </div>

            {/* ═══════════ Right: Result waterfall (3 cols) ═══════════ */}
            <div className="lg:col-span-3">
              {error ? (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
                  <AlertCircle size={28} className="mx-auto text-rose-500 mb-2" />
                  <p className="text-sm text-rose-700">{error}</p>
                </div>
              ) : !listingPrice || Number(listingPrice) <= 0 ? (
                <EmptyState />
              ) : !composedEstimate ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 flex items-center justify-center text-slate-400">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              ) : (
                <ResultPanel estimate={composedEstimate} estimating={estimating} />
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ─── Compose: overlay backend averages with rate-card precision ──────────────
//
// Backend returns the % based historical breakdown. We layer on top:
//   • Closing Fee   — always (static rate card)
//   • Easy Ship     — replace backend's % with weight-based ₹ when fulfillment=EASY_SHIP
//   • Referral Fee  — replace backend's % with SP-API exact ₹ when SP-API succeeded
//
// All recomputation happens client-side so the math stays consistent across all
// fee sources without a round-trip.

function composeEstimate({
  estimate, spApiFees, listingPrice, fulfillment, weightGrams, stepLevel, closingFeeGroup,
}) {
  const next = JSON.parse(JSON.stringify(estimate));
  const lines = next.waterfall;

  // Indexes we'll need
  const idxOf = (key) => lines.findIndex((l) => l.key === key);
  const sumLine = (key) => Number(lines[idxOf(key)]?.value) || 0;

  // ── Override Amazon commission (referral) if SP-API came through ──
  const usedSpApiReferral =
    spApiFees && spApiFees.source === 'sp_api' && spApiFees.referralFee != null;
  if (usedSpApiReferral) {
    const i = idxOf('commission');
    if (i >= 0) {
      const refFee = Number(spApiFees.referralFee);
      const refPct = spApiFees.referralPct
        ? `${Number(spApiFees.referralPct).toFixed(2)}%`
        : 'live';
      lines[i] = {
        ...lines[i],
        value: -Math.abs(refFee),
        hint: `${refPct} · live from Amazon SP-API for this ASIN`,
        source: 'sp_api',
      };
    }
  }

  // ── Override Easy Ship line with weight-based flat fee (gross-with-GST) ──
  // Amazon charges 18% GST on top of the published Easy Ship rate. The seller
  // can claim that GST as Input Tax Credit, but cash-flow-wise it leaves their
  // settlement now — so we display the gross amount, GST inclusive.
  if (fulfillment === 'EASY_SHIP') {
    const flatBase = lookupEasyShipFee(weightGrams, stepLevel);
    const flatGst = serviceGstOf(flatBase);
    const flatGross = withServiceGst(flatBase);
    const band = describeEasyShipBand(weightGrams);
    const stepLabel = (EASY_SHIP_STEP_LEVELS.find((s) => s.key === stepLevel) || {}).label || stepLevel;
    const i = idxOf('easy-ship');
    const line = {
      key: 'easy-ship',
      label: 'Easy Ship charge (incl GST)',
      value: -Math.abs(flatGross),
      kind: 'fee',
      hint: `₹${flatBase.toFixed(2)} base + ₹${flatGst.toFixed(2)} GST @${AMAZON_SERVICE_GST_PCT}% · ${band || 'enter weight'} · ${stepLabel} STEP`,
      source: 'rate_card',
    };
    if (i >= 0) {
      lines[i] = line;
    } else {
      // Insert after commission/fba-fee
      const after = idxOf('fba-fee') >= 0 ? idxOf('fba-fee') : idxOf('commission');
      lines.splice(after + 1, 0, line);
    }
  }

  // ── Insert Closing Fee (always — Amazon charges this for every order, +18% GST) ──
  const closingFeeBase = lookupClosingFee(listingPrice, closingFeeGroup);
  const closingFeeGst = serviceGstOf(closingFeeBase);
  const closingFeeGross = withServiceGst(closingFeeBase);
  const closingBand = describeClosingFeeBand(listingPrice);
  const closingGroupLabel =
    (CLOSING_FEE_GROUPS.find((g) => g.key === closingFeeGroup) || {}).label || 'Standard';
  const closingLine = {
    key: 'closing-fee',
    label: 'Closing fee (incl GST)',
    value: -Math.abs(closingFeeGross),
    kind: 'fee',
    hint: `₹${closingFeeBase.toFixed(2)} base + ₹${closingFeeGst.toFixed(2)} GST @${AMAZON_SERVICE_GST_PCT}% · ${closingBand} · ${closingGroupLabel}`,
    source: 'rate_card',
  };
  const existingClosingIdx = idxOf('closing-fee');
  if (existingClosingIdx >= 0) {
    lines[existingClosingIdx] = closingLine;
  } else {
    // Insert before TCS — closing fee belongs in the "Amazon kept" block
    const insertAt = idxOf('tcs') >= 0 ? idxOf('tcs') : lines.length - 1;
    lines.splice(insertAt, 0, closingLine);
  }

  // ── Recompute net deposit from the line items ──
  // Sum of all "fee" + "tax" lines (negative) minus what Amazon kept, starting from the
  // listing-price line (positive) and skipping passthrough/subtotal/total.
  let runningTotal = 0;
  for (const l of lines) {
    if (l.kind === 'in' || l.kind === 'fee' || l.kind === 'tax' || l.kind === 'passthrough') {
      runningTotal += Number(l.value) || 0;
    }
  }
  // runningTotal already accounts for: listingPrice − GST − commission − closing − shipping − TCS − TDS
  const bankDeposit = Math.round(runningTotal * 100) / 100;
  const totalIdx = idxOf('net-deposit');
  if (totalIdx >= 0) {
    lines[totalIdx] = { ...lines[totalIdx], value: bankDeposit };
  }

  // Update summary
  next.summary = {
    ...next.summary,
    bankDeposit,
    bankDepositPct:
      listingPrice > 0 ? Math.round((bankDeposit / listingPrice) * 1000) / 10 : 0,
  };

  // Track whether SP-API was used (so the rates panel can surface a green badge)
  next._meta = { usedSpApiReferral, closingFee: closingFeeGross };

  return next;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="bg-white rounded-xl shadow-sm border-2 border-dashed border-slate-200 p-10 text-center">
      <div className="bg-[#FFF4E6] rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
        <Calculator size={24} className="text-[#f08804]" />
      </div>
      <p className="text-sm font-semibold text-slate-700">Type a listing price to see your bank credit</p>
      <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
        We estimate Amazon's commission, FBA / Easy-Ship fees, closing fee, GST, TCS, and TDS withholdings — using your historical settlements and Amazon's official rate cards.
      </p>
    </div>
  );
}

function RatesPanel({ rates, spApiFees }) {
  if (!rates) return null;
  const usedSpApi = spApiFees && spApiFees.source === 'sp_api' && spApiFees.referralFee != null;
  const sourceLabel = {
    historical_sku:    { label: 'From this SKU', tone: 'emerald', hint: 'Most accurate — based on actual past sales of this SKU' },
    historical_seller: { label: 'From your account average', tone: 'amber',   hint: 'No history for this SKU — using your average across all products. Specific SKUs may differ.' },
    defaults:          { label: 'Industry defaults', tone: 'slate',   hint: 'No historical data yet — using conservative Amazon India defaults' },
  }[rates.source] || { label: rates.source, tone: 'slate', hint: '' };

  const palette = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    slate:   'bg-slate-50 border-slate-200 text-slate-700',
  }[sourceLabel.tone];

  return (
    <div className={`rounded-xl border p-3 ${palette}`}>
      <div className="flex items-start gap-2 mb-2">
        <Info size={12} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold flex items-center gap-1.5">
            Rates source: {sourceLabel.label}
            <Tooltip label="What this means">
              <div className="font-bold mb-1">Where the numbers come from</div>
              <p className="mb-1.5">{sourceLabel.hint}</p>
              <div className="text-amber-200 text-[10px]">
                Closing fee &amp; Easy Ship are always taken from Amazon's official rate cards (not your average).
                Referral fee uses historical average unless you pick a specific SKU — then we call SP-API for exact numbers.
              </div>
            </Tooltip>
          </p>
          <p className="text-[10px] opacity-80">{sourceLabel.hint}</p>
          {rates.sampleSize > 0 && (
            <p className="text-[10px] opacity-70 mt-0.5">
              Based on {rates.sampleSize} historical sale{rates.sampleSize === 1 ? '' : 's'} (last 180 days)
            </p>
          )}
          {usedSpApi && (
            <p className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
              <CheckCircle2 size={10} /> Referral fee live from Amazon SP-API
            </p>
          )}
          {spApiFees && spApiFees.source === 'unavailable' && (
            <p className="text-[10px] mt-1 italic opacity-70" title={spApiFees.message}>
              SP-API per-ASIN fees unavailable — using historical average
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1 text-[10px]">
        <RateItem label="Commission" value={fmtPct(rates.commissionPct)} />
        <RateItem label="FBA fee" value={fmtPct(rates.fbaPct)} />
        <RateItem label="Easy-Ship" value={fmtPct(rates.easyShipPct)} />
        <RateItem label="TCS" value={fmtPct(rates.tcsPct)} />
        <RateItem label="TDS 194-O" value={fmtPct(rates.tdsPct)} />
      </div>
    </div>
  );
}

function RateItem({ label, value }) {
  return (
    <div className="flex items-center justify-between bg-white/60 rounded px-2 py-1">
      <span className="opacity-70">{label}</span>
      <span className="font-bold tabular-nums">{value}</span>
    </div>
  );
}

function ResultPanel({ estimate, estimating }) {
  const { waterfall, summary } = estimate;
  const bankDeposit = Number(summary?.bankDeposit) || 0;
  const listingPrice = Number(summary?.listingPrice) || 0;
  const marginPct = Number(summary?.bankDepositPct) || 0;
  const goodMargin = marginPct >= 50;

  return (
    <div className="space-y-3 relative">
      {estimating && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 text-[10px] text-slate-500 bg-white/80 px-2 py-1 rounded">
          <Loader2 size={10} className="animate-spin" /> Updating…
        </div>
      )}

      {/* Hero — bank deposit + margin */}
      <div className={`rounded-xl border-2 p-5 ${
        goodMargin
          ? 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-300'
          : marginPct >= 30
            ? 'bg-gradient-to-br from-[#FFF4E6] to-[#FFE8C8] border-[#FFD89E]'
            : 'bg-gradient-to-br from-rose-50 to-rose-100/50 border-rose-300'
      }`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className={`text-[11px] font-semibold uppercase tracking-widest mb-1 ${
              goodMargin ? 'text-emerald-700' :
              marginPct >= 30 ? 'text-[#C45500]' : 'text-rose-700'
            }`}>
              Settlement deposit to bank
            </p>
            <p className="text-3xl sm:text-4xl font-black text-[#0F1111] tabular-nums">
              {fmt(bankDeposit)}
            </p>
            <p className="text-xs text-slate-700 mt-1.5">
              You keep <strong>{fmtPct(marginPct)}</strong> of the {fmt(listingPrice)} listing price
            </p>
          </div>
          <div className="flex flex-col items-end text-right">
            {goodMargin ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-white border border-emerald-200 rounded-full px-2 py-1">
                <CheckCircle2 size={11} /> Healthy margin
              </span>
            ) : marginPct < 30 ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-white border border-rose-200 rounded-full px-2 py-1">
                <TrendingDown size={11} /> Thin margin
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#C45500] bg-white border border-[#FFD89E] rounded-full px-2 py-1">
                <Sparkles size={11} /> Watchable
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Waterfall */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-900">How the rupees flow</h3>
          <span className="text-[10px] text-slate-400 italic">Mix of historical settlements + Amazon rate card</span>
        </div>
        <div className="space-y-1">
          {waterfall.map((line) => (
            <WaterfallLine key={line.key} line={line} />
          ))}
        </div>
      </div>

      {/* Footnote */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] text-blue-800 flex items-start gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          GST collected ({fmt(summary?.gstCollected)}) passes through to the government — it isn't yours.
          TCS + TDS withheld ({fmt(summary?.claimableTax)}) is claimable: TCS in your GSTR return, TDS in your income tax return.
        </span>
      </div>

      {/* Amazon service-fee GST footnote */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-[11px] text-emerald-800 flex items-start gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          All Amazon fees above include <strong>18% GST</strong> on top of the published rates (commission, closing fee, FBA, Easy Ship). This GST is recoverable as <strong>Input Tax Credit</strong> in your GSTR-3B — your CA can claim it against your output GST liability.
        </span>
      </div>
    </div>
  );
}

function WaterfallLine({ line }) {
  const { label, value, kind, hint, source } = line;
  const n = Number(value) || 0;
  const tone = {
    in:          { row: 'bg-emerald-50/40', text: 'text-emerald-700', icon: ArrowRight,  iconCls: 'text-emerald-500' },
    passthrough: { row: '',                 text: 'text-slate-500',   icon: ArrowRight,  iconCls: 'text-slate-300' },
    subtotal:    { row: 'border-y border-slate-200 my-1.5 py-1.5', text: 'text-slate-700 font-semibold', icon: null },
    fee:         { row: '',                 text: 'text-rose-700',    icon: ArrowRight,  iconCls: 'text-rose-400' },
    tax:         { row: '',                 text: 'text-amber-700',   icon: ArrowRight,  iconCls: 'text-amber-400' },
    total:       { row: 'bg-emerald-50 border-2 border-emerald-300 rounded-lg mt-2 px-2 py-2', text: 'text-emerald-900 font-black text-base', icon: Wallet, iconCls: 'text-emerald-600' },
  }[kind] || { row: '', text: 'text-slate-700', icon: null };

  const Icon = tone.icon;

  // Source badge — flags whether this line came from rate-card / SP-API / historical avg
  const sourceBadge = source === 'sp_api'
    ? { text: 'SP-API', cls: 'bg-emerald-100 text-emerald-700' }
    : source === 'rate_card'
      ? { text: 'Rate card', cls: 'bg-sky-100 text-sky-700' }
      : null;

  return (
    <div className={`flex items-center justify-between gap-3 px-2 ${tone.row}`}>
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon size={11} className={tone.iconCls} />}
        <span className={`${tone.text} text-xs flex-shrink-0`}>{label}</span>
        {sourceBadge && (
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${sourceBadge.cls}`}>
            {sourceBadge.text}
          </span>
        )}
        {hint && <span className="text-[10px] text-slate-400 italic truncate">· {hint}</span>}
      </div>
      <span className={`tabular-nums ${tone.text} flex-shrink-0`}>
        {fmt(n)}
      </span>
    </div>
  );
}
